import { urlToFile } from '@/utils/utils';
import { type EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import { FacilityDto } from '@shelflix/trpc/routes/facility/list/useCase';
import { MaterialDto, ScheduleCampaignDto } from '@shelflix/trpc/routes/material/get/useCase';
import { MaterialTypeDto } from '@shelflix/trpc/routes/materialType/list/useCase';
import { create } from 'zustand';

interface MaterialFormStore {
  formData: Partial<EditMaterialInputType>;
  setFormData: (data: Partial<EditMaterialInputType>) => void;
  resetForm: () => void;
}

export const useMaterialFormStore = create<MaterialFormStore>((set) => ({
  formData: {},
  setFormData: (data) => set((state) => ({ formData: { ...state.formData, ...data } })),
  resetForm: () => set({ formData: {} }),
}));

interface ScheduleCampaign {
  campaign: CampaignDto;
  startDate: string;
  isPaused?: boolean;
  endDate: string;
  displayFormat: string;
}

const initialState = {
  isSubmitting: false,
  isLoadingMaterial: false,
  openModal: null,
  contract: null as File | null,
  selectedFacility: null as Pick<FacilityDto, 'uuid' | 'name' | 'city' | 'state' | 'cnpj' | 'address'> | null,
  selectedMaterialType: null as MaterialTypeDto | null,
  brandUuid: null as string | null,
  selectedCampaign: null as CampaignDto | null,
  defaultCampaign: null as CampaignDto | null,
  currentCampaign: null as CampaignDto | null,
  scheduledCampaigns: [] as ScheduleCampaign[],
  availableCampaign: [] as CampaignDto[],
  searchMaterialType: '',
  hasCampaignsChangedByUser: false,
};
interface MaterialEditState {
  // Estados de controle
  isSubmitting: boolean;
  isLoadingMaterial: boolean;

  // Estados de UI
  openModal: {
    type: 'play_now' | 'scheduled' | 'default';
    title: string;
    icon: string;
  } | null;

  // Dados do formulário
  contract: File | null;
  selectedFacility: Pick<FacilityDto, 'uuid' | 'name' | 'city' | 'state' | 'cnpj' | 'address'> | null;
  selectedMaterialType: MaterialTypeDto | null;
  brandUuid: string | null;

  // Campanhas
  selectedCampaign: CampaignDto | null;
  defaultCampaign: CampaignDto | null;
  currentCampaign: CampaignDto | null;
  scheduledCampaigns: ScheduleCampaign[];
  availableCampaign: CampaignDto[];
  hasCampaignsChangedByUser: boolean;

  // Actions
  initializeMaterial: (materialToEdit: MaterialDto) => Promise<void>;
  setSubmitting: (isSubmitting: boolean) => void;
  setLoadingMaterial: (isLoading: boolean) => void;
  setOpenModal: (
    openModal: {
      type: 'play_now' | 'scheduled' | 'default';
      title: string;
      icon: string;
    } | null
  ) => void;
  setContract: (file: File | null) => void;
  setSelectedFacility: (facility: FacilityDto | null) => void;
  setSelectedCampaign: (campaign: CampaignDto | null) => void;
  setSelectedMaterialType: (materialType: MaterialTypeDto | null) => void;
  addDefaultCampaign: (campaign: CampaignDto) => void;
  addScheduledCampaign: (campaign: CampaignDto, startDate: string, endDate: string) => void;
  removeScheduledCampaign: (campaign: CampaignDto) => void;
  removeCurrentCampaign: (campaign: CampaignDto) => void;
  setCurrentCampaign: (campaign: CampaignDto | null) => void;
  clearScheduledCampaign: (campaign: CampaignDto) => void;
  setListScheduledCampaigns: (scheduledCampaigns: ScheduleCampaign[]) => void;
  setHasCampaignsChangedByUser: (changed: boolean) => void;
  reset: () => void;
}

export const useMaterialEditStore = create<MaterialEditState>((set) => ({
  // Estado inicial
  ...initialState,

  // Actions
  initializeMaterial: async (materialToEdit) => {
    set({ isLoadingMaterial: true });

    try {
      const contractFile = await urlToFile(materialToEdit.contractUrl, `contract-${materialToEdit.name}`);

      set({
        contract: contractFile,
        selectedFacility: {
          uuid: materialToEdit.facility.uuid,
          address: materialToEdit.facility.address,
          city: materialToEdit.facility.city,
          state: materialToEdit.facility.state,
          cnpj: materialToEdit.cnpj,
          name: materialToEdit.facility.name,
        },
        selectedMaterialType: {
          active: true,
          name: materialToEdit.materialType.name,
          uuid: materialToEdit.uuid,
          brand: {
            ...materialToEdit.brand,
            code: '',
          },
          photoUrl: materialToEdit.logoUrl,
        },
        defaultCampaign: materialToEdit.defaultCampaign
          ? {
              ...materialToEdit.defaultCampaign,
              materialCount: 0,
              campaignMedias: materialToEdit.defaultCampaign.medias ?? [],
            }
          : null,
        currentCampaign: materialToEdit.currentCampaign
          ? {
              ...materialToEdit.currentCampaign,
              materialCount: 0,
              campaignMedias: materialToEdit.currentCampaign?.medias ?? [],
            }
          : null,
        scheduledCampaigns:
          materialToEdit.scheduleCampaigns?.map((item: ScheduleCampaignDto) => ({
            campaign: {
              ...item.campaign,
              materialCount: 0,
              campaignMedias: item.campaign.medias ?? [],
            },
            displayFormat: item.campaign.displayFormat,
            isPaused: item.isPaused,
            startDate: item.startDate,
            endDate: item.endDate,
          })) || [],
        isLoadingMaterial: false,
      });
    } catch (error) {
      set({ isLoadingMaterial: false });
      throw error;
    }
  },

  setSubmitting: (isSubmitting) => set({ isSubmitting }),
  setLoadingMaterial: (isLoadingMaterial) => set({ isLoadingMaterial }),
  setOpenModal: (openModal) => set({ openModal }),
  setContract: (contract) => set({ contract }),
  setSelectedFacility: (selectedFacility) => set({ selectedFacility }),
  setSelectedCampaign: (selectedCampaign) => set({ selectedCampaign }),
  setSelectedMaterialType: (selectedMaterialType) =>
    set({
      selectedMaterialType,
      brandUuid: selectedMaterialType?.brand.uuid || null,
    }),

  addDefaultCampaign: (campaign) =>
    set((state) => ({
      defaultCampaign: campaign,
      availableCampaign: state.availableCampaign.filter((item) => item.uuid !== campaign.uuid),
      hasCampaignsChangedByUser: true,
    })),

  addScheduledCampaign: (campaign, startDate, endDate) =>
    set((state) => ({
      scheduledCampaigns: [
        ...state.scheduledCampaigns,
        { campaign, startDate, endDate, displayFormat: campaign.displayFormat },
      ],
      availableCampaign: state.availableCampaign.filter((item) => item.uuid !== campaign.uuid),
      openModal: null,
      hasCampaignsChangedByUser: true,
    })),

  removeScheduledCampaign: (campaign) =>
    set((state) => ({
      scheduledCampaigns: state.scheduledCampaigns.filter((c) => c.campaign.uuid !== campaign.uuid),
      availableCampaign: state.availableCampaign.some((c) => c.uuid === campaign.uuid)
        ? state.availableCampaign
        : [...state.availableCampaign, campaign],
      hasCampaignsChangedByUser: true,
    })),
  clearScheduledCampaign: (campaign: CampaignDto) =>
    set((state) => ({
      scheduledCampaigns: state.scheduledCampaigns.filter((c) => c.campaign.uuid === campaign.uuid),
      availableCampaign: state.availableCampaign.some((c) => c.uuid === campaign.uuid)
        ? state.availableCampaign
        : [...state.availableCampaign, campaign],
      currentCampaign: campaign,
    })),
  removeCurrentCampaign: (campaign) =>
    set((state) => {
      // Safety check: if campaign is null/undefined, just reset to default campaign
      if (!campaign) {
        return { currentCampaign: state.defaultCampaign, hasCampaignsChangedByUser: true };
      }
      return {
        scheduledCampaigns: state.scheduledCampaigns.filter((c) => c.campaign.uuid !== campaign.uuid),
        availableCampaign: state.availableCampaign.some((c) => c.uuid === campaign.uuid)
          ? state.availableCampaign
          : [...state.availableCampaign, campaign],
        currentCampaign: state.defaultCampaign,
        hasCampaignsChangedByUser: true,
      };
    }),

  setListScheduledCampaigns: (scheduledCampaigns) => set({ scheduledCampaigns }),

  setCurrentCampaign: (currentCampaign) => set({ currentCampaign }),

  setHasCampaignsChangedByUser: (hasCampaignsChangedByUser) => set({ hasCampaignsChangedByUser }),

  reset: () =>
    set({
      ...initialState,
      isSubmitting: false,
      isLoadingMaterial: false,
    }),
}));
